import { useRef, useState } from "react";
import { ToolContainer, FileDropZone, downloadBlob } from "@/components/ToolLayout";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { effectiveDpi, pngToBleedPdfBytes } from "@/lib/printExport";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Download, Plus, X } from "lucide-react";
import { ToolInfoSection } from "@/components/ToolInfoSection";

const PRESETS = {
  "Web Banner": { w: 1200, h: 300 },
  "Social Post": { w: 1080, h: 1080 },
  "Storefront Sign": { w: 1200, h: 800 },
  "Large Poster": { w: 1000, h: 1400 },
  "Instagram Story": { w: 1080, h: 1920 },
  "YouTube Thumbnail": { w: 1280, h: 720 },
  "YouTube Channel Banner": { w: 2560, h: 1440 },
  "Facebook Cover": { w: 851, h: 315 },
  "X Post": { w: 1200, h: 675 },
  "LinkedIn Post": { w: 1200, h: 627 },
  "Yard Sign (24x18in)": { w: 3600, h: 2700 },
  "Flyer (A4)": { w: 1240, h: 1754 },
} as const;

// A small, genuinely distinct set rather than every system font --
// each one renders visibly differently (serif vs sans vs a rounded
// display face), so the choice is meaningful rather than cosmetic
// noise between near-identical options.
const FONT_OPTIONS = [
  { label: "Arial (default)", value: "Arial, sans-serif" },
  { label: "Georgia (serif)", value: "Georgia, 'Times New Roman', serif" },
  { label: "Verdana (wide)", value: "Verdana, Geneva, sans-serif" },
  { label: "Trebuchet (rounded)", value: "'Trebuchet MS', sans-serif" },
] as const;

const GRADIENTS: [string, string][] = [
  ["#4F46E5", "#A855F7"],
  ["#059669", "#34D399"],
  ["#DC2626", "#F97316"],
  ["#0891B2", "#06B6D4"],
  ["#1E293B", "#475569"],
  ["#DB2777", "#F472B6"],
];

const TOOL_INFO_CONTENT = {
  what: `Designs web banners, storefront signs, and posters at any custom pixel size, with size presets for common use cases — including an optional background photo and logo/image overlay — exporting as a real PNG image file.`,
  how: `Choose a size preset or enter custom dimensions, add your heading, subheading, and optional footer text, pick a color scheme or upload a background photo, optionally add a logo or image, and download the finished banner as a PNG.`,
  tip: `This covers text-and-color banner design well, with real support for a background photo and a logo/image overlay — for a design built entirely from custom illustration rather than a photo or logo you already have, that needs an illustration or photo editor, which is a different kind of tool. Pixel dimensions are exact, and the Print PDF section shows the DPI you'll get at the finished width you enter and can add 0.125" bleed for the print shop.`,
};

export default function BannerSignMaker() {
  const [preset, setPreset] = useState<keyof typeof PRESETS>("Web Banner");
  const [width, setWidth] = useState(String(PRESETS["Web Banner"].w));
  const [height, setHeight] = useState(String(PRESETS["Web Banner"].h));
  const [heading, setHeading] = useState("Grand Opening");
  const [subheading, setSubheading] = useState(
    "Visit us this weekend for special offers"
  );
  const [footer, setFooter] = useState("Mon–Sat 9AM–7PM  •  (555) 123-4567");
  const [gradient, setGradient] = useState<[string, string]>(GRADIENTS[0]);
  const [fontFamily, setFontFamily] = useState<string>(FONT_OPTIONS[0].value);
  const [backgroundImage, setBackgroundImage] = useState<string | null>(null);
  const [logoImage, setLogoImage] = useState<string | null>(null);
  const [points, setPoints] = useState<string[]>([]);
  const svgRef = useRef<SVGSVGElement>(null);
  const [printWidthIn, setPrintWidthIn] = useState("24");
  const [withBleed, setWithBleed] = useState(true);

  const handleBackgroundUpload = (files: File[]) => {
    const file = files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setBackgroundImage(reader.result as string);
    reader.readAsDataURL(file);
  };

  const handleLogoUpload = (files: File[]) => {
    const file = files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setLogoImage(reader.result as string);
    reader.readAsDataURL(file);
  };

  const addPoint = () => setPoints(prev => [...prev, ""]);
  const updatePoint = (i: number, value: string) =>
    setPoints(prev => prev.map((p, idx) => (idx === i ? value : p)));
  const removePoint = (i: number) =>
    setPoints(prev => prev.filter((_, idx) => idx !== i));

  const applyPreset = (p: keyof typeof PRESETS) => {
    setPreset(p);
    setWidth(String(PRESETS[p].w));
    setHeight(String(PRESETS[p].h));
  };

  const W = Math.max(100, parseInt(width, 10) || 1200);
  const H = Math.max(100, parseInt(height, 10) || 300);
  const headingSize = Math.min(W, H) * 0.09;
  const subSize = headingSize * 0.4;
  const footerSize = headingSize * 0.28;

  // Draws the banner at its exact pixel size and hands back a PNG.
  const renderPng = () =>
    new Promise<Blob>((resolve, reject) => {
      if (!svgRef.current) return reject(new Error("no banner"));
      const svgData = new XMLSerializer().serializeToString(svgRef.current);
      const url = URL.createObjectURL(new Blob([svgData], { type: "image/svg+xml" }));
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement("canvas");
        canvas.width = W;
        canvas.height = H;
        canvas.getContext("2d")!.drawImage(img, 0, 0, W, H);
        URL.revokeObjectURL(url);
        canvas.toBlob(blob => (blob ? resolve(blob) : reject(new Error("canvas"))), "image/png");
      };
      img.onerror = () => { URL.revokeObjectURL(url); reject(new Error("render")); };
      img.src = url;
    });

  const download = async () => {
    try {
      downloadBlob(await renderPng(), "banner.png");
      toast.success("Banner downloaded");
    } catch {
      toast.error("Couldn't render the banner — try removing the background photo or logo and download again");
    }
  };

  const printWidth = parseFloat(printWidthIn.replace(",", "."));
  const dpi = effectiveDpi(W, printWidth);
  const downloadPrintPdf = async () => {
    if (!(printWidth >= 2 && printWidth <= 120)) { toast.error("Enter the finished printed width in inches (2–120)"); return; }
    try {
      const bytes = await pngToBleedPdfBytes(await renderPng(), printWidth, withBleed ? 0.125 : 0);
      downloadBlob(new Blob([bytes as BlobPart], { type: "application/pdf" }), withBleed ? "banner-print-bleed.pdf" : "banner-print.pdf");
      toast.success("Print PDF downloaded");
    } catch {
      toast.error("Couldn't build the PDF — try a smaller size or remove the background photo");
    }
  };

  return (
    <ToolContainer>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div className="space-y-4">
          <div>
            <Label className="mb-1 block text-sm">Size Preset</Label>
            <div className="flex flex-wrap gap-2">
              {(Object.keys(PRESETS) as (keyof typeof PRESETS)[]).map(p => (
                <Button
                  key={p}
                  size="sm"
                  variant={preset === p ? "default" : "outline"}
                  onClick={() => applyPreset(p)}
                >
                  {p}
                </Button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="mb-1 block text-sm">Width (px)</Label>
              <Input aria-label="Width (px)" value={width} onChange={e => setWidth(e.target.value)} />
            </div>
            <div>
              <Label className="mb-1 block text-sm">Height (px)</Label>
              <Input aria-label="Height (px)" value={height} onChange={e => setHeight(e.target.value)} />
            </div>
          </div>
          <div>
            <Label className="mb-1 block text-sm">Heading</Label>
            <Input aria-label="Heading" value={heading} onChange={e => setHeading(e.target.value)} />
          </div>
          <div>
            <Label className="mb-1 block text-sm">Subheading</Label>
            <Input aria-label="Subheading"
              value={subheading}
              onChange={e => setSubheading(e.target.value)}
            />
          </div>
          <div>
            <Label className="mb-1 block text-sm">
              Footer (hours, contact, etc. — optional)
            </Label>
            <Input value={footer} onChange={e => setFooter(e.target.value)} />
          </div>
          <div>
            <Label className="mb-1 block text-sm">Color</Label>
            <div className="flex gap-2">
              {GRADIENTS.map((g, i) => (
                <button
                  key={i}
                  onClick={() => setGradient(g)}
                  className={`w-9 h-9 rounded-lg border-2 ${gradient === g ? "border-foreground" : "border-transparent"}`}
                  style={{
                    background: `linear-gradient(135deg, ${g[0]}, ${g[1]})`,
                  }}
                />
              ))}
            </div>
          </div>
          <div>
            <Label className="mb-1 block text-sm">Font</Label>
            <Select value={fontFamily} onValueChange={setFontFamily}>
              <SelectTrigger className="w-56"><SelectValue /></SelectTrigger>
              <SelectContent>
                {FONT_OPTIONS.map(f => (
                  <SelectItem key={f.value} value={f.value} style={{ fontFamily: f.value }}>{f.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="mb-1 block text-sm">Background Photo (optional)</Label>
            {backgroundImage ? (
              <div className="flex items-center gap-2">
                <img src={backgroundImage} className="h-10 w-16 object-cover rounded border" />
                <Button size="sm" variant="ghost" onClick={() => setBackgroundImage(null)}>
                  <X className="h-3.5 w-3.5 mr-1" /> Remove
                </Button>
              </div>
            ) : (
              <FileDropZone gateAtDownload accept="image/*" onFiles={handleBackgroundUpload} label="Drop a background photo" />
            )}
          </div>
          <div>
            <Label className="mb-1 block text-sm">Logo / Image (optional)</Label>
            {logoImage ? (
              <div className="flex items-center gap-2">
                <img src={logoImage} className="h-10 w-16 object-contain rounded border bg-white" />
                <Button size="sm" variant="ghost" onClick={() => setLogoImage(null)}>
                  <X className="h-3.5 w-3.5 mr-1" /> Remove
                </Button>
              </div>
            ) : (
              <FileDropZone gateAtDownload accept="image/*" onFiles={handleLogoUpload} label="Drop a logo or product image" />
            )}
          </div>
          <div>
            <div className="flex items-center justify-between mb-1">
              <Label className="text-sm">Extra Info Points (optional)</Label>
              <Button size="sm" variant="ghost" onClick={addPoint} className="h-7 gap-1">
                <Plus className="h-3.5 w-3.5" /> Add Point
              </Button>
            </div>
            <div className="space-y-2">
              {points.map((point, i) => (
                <div key={i} className="flex gap-2">
                  <Input
                    value={point}
                    onChange={e => updatePoint(i, e.target.value)}
                    placeholder="e.g. Free parking available"
                  />
                  <Button size="icon" variant="ghost" onClick={() => removePoint(i)} className="shrink-0">
                    <X className="h-3.5 w-3.5" />
                  </Button>
                </div>
              ))}
            </div>
          </div>
          <Button className="w-full" onClick={download}>
            <Download className="h-4 w-4 mr-2" /> Download PNG
          </Button>
          <div className="rounded-md border p-3 space-y-2">
            <p className="text-sm font-medium">Print PDF</p>
            <div className="flex items-center gap-2 text-sm">
              <label htmlFor="print-width" className="whitespace-nowrap">Finished width (in)</label>
              <input id="print-width" inputMode="decimal" value={printWidthIn} onChange={e => setPrintWidthIn(e.target.value)} className="h-8 w-20 rounded-md border bg-background px-2 text-sm" />
              <label className="flex items-center gap-1 ml-2"><input type="checkbox" checked={withBleed} onChange={e => setWithBleed(e.target.checked)} /> 0.125" bleed</label>
            </div>
            {printWidth > 0 && (
              <p className={`text-xs ${dpi < 150 ? "text-amber-600" : "text-muted-foreground"}`}>
                {W} × {H} px printed {printWidth} in wide = {Math.round(dpi)} DPI{dpi < 150 ? " — below 150 DPI will look soft in print; pick a larger size above or a smaller finished width" : dpi >= 300 ? " — print quality" : " — fine for large signs viewed from a distance"}.
              </p>
            )}
            <Button className="w-full" variant="outline" onClick={downloadPrintPdf}>Download print PDF</Button>
          </div>
          <p className="text-xs text-muted-foreground">
            For custom illustrated or photo-based ad artwork (not just text +
            color), that needs a separate illustration or photo editor — this tool covers
            text-and-color banners, signs, and posters.
          </p>
        </div>

        <div className="flex items-center justify-center overflow-auto rounded-lg border bg-muted/20 p-4">
          <svg
            ref={svgRef}
            viewBox={`0 0 ${W} ${H}`}
            style={{ width: "100%", maxWidth: 500, height: "auto" }}
          >
            <defs>
              <linearGradient
                id="bannerGrad"
                x1="0%"
                y1="0%"
                x2="100%"
                y2="100%"
              >
                <stop offset="0%" stopColor={gradient[0]} />
                <stop offset="100%" stopColor={gradient[1]} />
              </linearGradient>
            </defs>
            {backgroundImage && (
              <image href={backgroundImage} x="0" y="0" width={W} height={H} preserveAspectRatio="xMidYMid slice" />
            )}
            <rect width={W} height={H} fill="url(#bannerGrad)" opacity={backgroundImage ? 0.55 : 1} />
            {logoImage && (
              <image
                href={logoImage}
                x={W / 2 - headingSize * 0.9}
                y={H * 0.08}
                width={headingSize * 1.8}
                height={headingSize * 1.8}
                preserveAspectRatio="xMidYMid meet"
              />
            )}
            <text
              x={W / 2}
              y={H / 2 - headingSize * 0.3}
              textAnchor="middle"
              fontFamily={fontFamily}
              fontSize={headingSize}
              fontWeight="800"
              fill="#fff"
            >
              {heading}
            </text>
            <text
              x={W / 2}
              y={H / 2 + subSize * 1.3}
              textAnchor="middle"
              fontFamily={fontFamily}
              fontSize={subSize}
              fill="#fff"
              opacity="0.9"
            >
              {subheading}
            </text>
            {points.filter(Boolean).map((point, i) => (
              <text
                key={i}
                x={W / 2}
                y={H / 2 + subSize * 2.6 + i * footerSize * 1.6}
                textAnchor="middle"
                fontFamily={fontFamily}
                fontSize={footerSize}
                fill="#fff"
                opacity="0.95"
              >
                • {point}
              </text>
            ))}
            {footer && (
              <text
                x={W / 2}
                y={H - footerSize * 1.5}
                textAnchor="middle"
                fontFamily={fontFamily}
                fontSize={footerSize}
                fill="#fff"
                opacity="0.8"
              >
                {footer}
              </text>
            )}
          </svg>
        </div>
      </div>
          <ToolInfoSection whatIsIt={TOOL_INFO_CONTENT.what} howToUse={TOOL_INFO_CONTENT.how} tip={TOOL_INFO_CONTENT.tip} />
    </ToolContainer>
  );
}
